import {
  createContext,
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import {
  collection,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./context/AuthContext";

export const CartContext = createContext({
  items: [],
  wishlist: [],
  getProductQuantity: () => {},
  addOneToCart: () => {},
  removeOneFromCart: () => {},
  deleteFromCart: () => {},
  getTotalCost: () => {},
  addToWishlist: () => {},
  removeFromWishlist: () => {},
});

export function CartProvider({ children }) {
  const [cartProducts, setCartProducts] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const { currentUser } = useAuth() || { currentUser: null };

  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart"));
    if (storedCart) {
      setCartProducts(storedCart);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartProducts));
  }, [cartProducts]);

  useEffect(() => {
    if (currentUser) {
      const fetchWishlist = async () => {
        try {
          const docRef = doc(db, "wishlists", currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setWishlist(docSnap.data().items || []);
          }
        } catch (error) {
          console.error("Error fetching wishlist: ", error);
        }
      };
      fetchWishlist();
    }
  }, [currentUser]);

  const getProductData = useCallback(async (id) => {
    try {
      const productRef = doc(collection(db, "products"), id);
      const productSnap = await getDoc(productRef);
      if (productSnap.exists()) {
        return { id: productSnap.id, ...productSnap.data() };
      } else {
        console.log("No such product!");
        return null;
      }
    } catch (error) {
      console.error("Error fetching product: ", error);
      return null;
    }
  }, []);

  const getProductQuantity = useCallback(
    (id) => {
      const product = cartProducts.find((product) => product.id === id);
      return product ? product.quantity : 0;
    },
    [cartProducts]
  );

  const addOneToCart = useCallback(
    async (id, size = "30ml") => {
      const existingProduct = cartProducts.find(
        (product) => product.id === id && product.size === size
      );

      if (!existingProduct) {
        const productToAdd = await getProductData(id);
        if (productToAdd) {
          const price = getProductPrice(size);
          setCartProducts((prev) => [
            ...prev,
            { ...productToAdd, quantity: 1, size, price },
          ]);
        }
      } else {
        setCartProducts((prev) =>
          prev.map((product) =>
            product.id === id && product.size === size
              ? { ...product, quantity: product.quantity + 1 }
              : product
          )
        );
      }
    },
    [cartProducts, getProductData]
  );

  const removeOneFromCart = useCallback(
    (id) => {
      const quantity = getProductQuantity(id);

      if (quantity === 1) {
        deleteFromCart(id);
      } else {
        setCartProducts((prev) =>
          prev.map((product) =>
            product.id === id
              ? { ...product, quantity: product.quantity - 1 }
              : product
          )
        );
      }
    },
    [cartProducts, getProductQuantity]
  );

  const getProductPrice = useCallback((size) => {
    switch (size) {
      case "30ml":
        return 12;
      case "50ml":
        return 17;
      case "100ml":
        return 29;
      default:
        return 12;
    }
  }, []);

  const deleteFromCart = useCallback((id) => {
    setCartProducts((prev) => prev.filter((product) => product.id !== id));
  }, []);

  const getTotalCost = useCallback(async () => {
    let totalCost = 0;
    for (const cartItem of cartProducts) {
      const productData = await getProductData(cartItem.id);
      if (productData) {
        const price = cartItem.selectedSize
          ? productData.prices[cartItem.selectedSize]
          : productData.price;
        totalCost += price * cartItem.quantity;
      }
    }
    return totalCost;
  }, [cartProducts, getProductData]);

  const addToWishlist = useCallback(
    async (product) => {
      if (!currentUser) return false;

      // Build a clean object with no undefined fields (Firestore rejects undefined)
      const cleanProduct = {
        id: product.id,
        name: product.name || "",
        image: product.image || "",
        category: product.category || "",
        seasons: Array.isArray(product.seasons) ? product.seasons : [],
        price: product.price || 12,
      };

      try {
        const docRef = doc(db, "wishlists", currentUser.uid);
        await setDoc(
          docRef,
          { items: arrayUnion(cleanProduct) },
          { merge: true }
        );
        setWishlist((prev) => {
          if (prev.find((item) => item.id === cleanProduct.id)) return prev;
          return [...prev, cleanProduct];
        });
        return true;
      } catch (error) {
        console.error("Error adding to wishlist: ", error);
        return false;
      }
    },
    [currentUser]
  );

  const removeFromWishlist = useCallback(
    async (productId) => {
      if (!currentUser) return;

      try {
        const docRef = doc(db, "wishlists", currentUser.uid);
        const productToRemove = wishlist.find((item) => item.id === productId);
        if (productToRemove) {
          await updateDoc(docRef, {
            items: arrayRemove(productToRemove),
          });
          setWishlist((prev) => prev.filter((item) => item.id !== productId));
        }
      } catch (error) {
        console.error("Error removing from wishlist: ", error);
      }
    },
    [currentUser, wishlist]
  );

  const contextValue = {
    items: cartProducts,
    wishlist,
    getProductQuantity,
    addOneToCart,
    removeOneFromCart,
    deleteFromCart,
    getTotalCost,
    addToWishlist,
    removeFromWishlist,
    getProductData,
  };

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}

export default CartProvider;
